import type { FC } from "react";

import { Box, Column, Heading, Row, Text, Tooltip } from "@hightouch/htui";

import {
  formatDateLong,
  formatRelativeShort,
  formatTime,
} from "src/utils/time";

import { barColor, getDisplayNameForResource } from "./resource-display";

type Props = {
  directDependents: number;
  indirectDependents: number;
  countsByType: ReadonlyMap<string, number>;
  lastCalculatedAt: Date | null;
};

export const UsageSummary: FC<Props> = ({
  directDependents,
  indirectDependents,
  countsByType,
  lastCalculatedAt,
}) => {
  const total = directDependents + indirectDependents;
  const segments = [...countsByType.entries()]
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);
  const segmentTotal = segments.reduce((sum, [, count]) => sum + count, 0);

  return (
    <Column gap={2}>
      <Row align="flex-end" justify="space-between" gap={6} flexWrap="wrap">
        <Row align="center" gap={2.5} flexShrink={0}>
          <Heading
            // Metric text borrows the heading type scale without taking heading
            // semantics, since a metric is a readout rather than a section title.
            as="span"
            size="2xl"
            color={total === 0 ? "text.disabled" : "text.primary"}
          >
            {total.toLocaleString()}
          </Heading>
          <Column>
            <Heading as="span" size="md">
              Total usage
            </Heading>
            {lastCalculatedAt && (
              <Tooltip
                message={`${formatDateLong(lastCalculatedAt)} at ${formatTime(lastCalculatedAt)}`}
              >
                <Text size="sm" color="text.secondary">
                  Last calculated {formatCalculatedAgo(lastCalculatedAt)}
                </Text>
              </Tooltip>
            )}
          </Column>
        </Row>
        <Row
          align="center"
          gap={5}
          sx={{
            // A separator inside a line of text, so it takes a text color
            // rather than a surface border color.
            columnRule: "1px solid var(--chakra-colors-text-tertiary)",
            // Trims the rule from the 24px row height to 14px.
            columnRuleInset: "5px",
          }}
        >
          <SummaryMetric
            label="Direct"
            value={directDependents}
            tooltip="Resources that reference this one directly."
          />
          <SummaryMetric
            label="Indirect"
            value={indirectDependents}
            tooltip="Resources that reach this one through another resource."
          />
        </Row>
      </Row>

      {segmentTotal > 0 && (
        <Row
          height={4}
          borderRadius="sm"
          overflow="hidden"
          gap="1px"
          aria-hidden
        >
          {segments.map(([resourceType, count]) => {
            const display = getDisplayNameForResource(resourceType);
            return (
              <Tooltip
                key={resourceType}
                message={`${display.pluralLabel} ${count.toLocaleString()}`}
              >
                <Box
                  // Proportional to the subtree, not to the rows loaded.
                  flex={count}
                  bg={barColor(display.color)}
                />
              </Tooltip>
            );
          })}
        </Row>
      )}
    </Column>
  );
};

// The relative time continues "Last calculated", so the capitalized
// under-an-hour reading goes lowercase.
function formatCalculatedAgo(calculatedAt: Date) {
  const relative = formatRelativeShort(calculatedAt.toISOString());
  return relative === "Just now" ? "just now" : relative;
}

type SummaryMetricProps = {
  label: string;
  value: number;
  tooltip: string;
};

function SummaryMetric({ label, value, tooltip }: SummaryMetricProps) {
  return (
    <Tooltip message={tooltip}>
      <Row align="center" gap={1.5} flexShrink={0}>
        <Heading
          as="span"
          size="md"
          color={value === 0 ? "text.disabled" : "text.primary"}
        >
          {value.toLocaleString()}
        </Heading>
        <Text>{label}</Text>
      </Row>
    </Tooltip>
  );
}
